import {
  Button,
  ColorInput,
  FileInput,
  Group,
  Image,
  Input,
  NumberInput,
  SegmentedControl,
  Select,
  SimpleGrid,
  Switch,
  Textarea,
  TextInput,
} from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import {
  AdminForm,
  AdminFormActions,
  AdminFormModal,
  AdminFormSection,
} from "../../../components/AdminForm.tsx";
import type { EditForm } from "./types.ts";
import { PRIZE_TYPES } from "./types.ts";

type Props = {
  editingId: string | null;
  editingName: string;
  onClose: () => void;
  form: UseFormReturnType<EditForm>;
  isPending: boolean;
  imageUploading: boolean;
  onSubmit: (v: EditForm) => void;
  onImageUpload: (file: File | null) => void;
  languages: readonly string[];
};

export function EditPrizeModal({
  editingId,
  editingName,
  onClose,
  form,
  isPending,
  imageUploading,
  onSubmit,
  onImageUpload,
  languages,
}: Props) {
  return (
    <AdminFormModal
      title={`编辑：${editingName}`}
      opened={editingId !== null}
      onClose={onClose}
      size="xl"
    >
      <AdminForm onSubmit={form.onSubmit(onSubmit)}>
        <AdminFormSection title="基本信息">
          <Select label="类型" data={PRIZE_TYPES} {...form.getInputProps("type")} />
          <TextInput label="名称" {...form.getInputProps("name")} />
          <Textarea
            label="描述（可选，运营备注）"
            rows={2}
            {...form.getInputProps("description")}
          />
          <Switch
            label="启用"
            checked={form.values.isEnabled}
            onChange={(e) => form.setFieldValue("isEnabled", e.currentTarget.checked)}
          />
        </AdminFormSection>

        <AdminFormSection title="价值与库存">
          <SimpleGrid cols={2}>
            <NumberInput label="权重" min={0} {...form.getInputProps("weight")} />
            <NumberInput
              label={form.values.type === "extra_spins" ? "价值（赠送抽奖次数）" : "价值（金额）"}
              min={0}
              {...form.getInputProps("valueAmount")}
            />
            <TextInput
              label="货币（virtual / physical 现金奖填，如 INR）"
              placeholder="INR"
              {...form.getInputProps("valueCurrency")}
            />
            <TextInput label="库存" {...form.getInputProps("stock")} />
            <TextInput label="日上限" {...form.getInputProps("dailyLimit")} />
          </SimpleGrid>
          <Switch
            label="设为大奖（jackpot：中奖触发广播 + 大奖弹窗）"
            checked={form.values.isJackpot}
            onChange={(e) => form.setFieldValue("isJackpot", e.currentTarget.checked)}
          />
        </AdminFormSection>

        {form.values.type === "virtual" || form.values.type === "physical" ? (
          <AdminFormSection title="派彩与领奖">
            {form.values.type === "virtual" ? (
              <Switch
                label="WG Pay 自动派彩（virtual 现金奖，绑定用户中奖即派彩）"
                checked={form.values.wgPay}
                onChange={(e) => form.setFieldValue("wgPay", e.currentTarget.checked)}
              />
            ) : null}
            {form.values.type === "virtual" && form.values.wgPay ? (
              <NumberInput
                label="稽核倍率（WG 流水要求倍数，1 = 无要求）"
                min={1}
                step={0.5}
                decimalScale={2}
                {...form.getInputProps("auditRate")}
              />
            ) : null}
            {languages.map((lang) => (
              <Textarea
                key={`ci-${lang}`}
                label={`领奖说明 · ${lang}（展示在中奖弹窗）`}
                rows={2}
                value={form.values.claimInstructions[lang] ?? ""}
                onChange={(e) =>
                  form.setFieldValue("claimInstructions", {
                    ...form.values.claimInstructions,
                    [lang]: e.currentTarget.value,
                  })
                }
              />
            ))}
            {form.values.type === "physical" ? (
              <Switch
                label="实物需要收件"
                checked={form.values.requiresShipping}
                onChange={(e) => form.setFieldValue("requiresShipping", e.currentTarget.checked)}
              />
            ) : null}
          </AdminFormSection>
        ) : null}

        <AdminFormSection title="扇区展示">
          <Input.Wrapper
            label="标题模式"
            description="文字 = 扇区展示名称；图片 = 仅展示上传的图片；图片+文字 = 图片 + 名称同时展示"
          >
            <SegmentedControl
              fullWidth
              mt={4}
              data={[
                { label: "文字", value: "text" },
                { label: "图片", value: "image" },
                { label: "图片+文字", value: "image_text" },
              ]}
              value={form.values.titleMode}
              onChange={(v) =>
                form.setFieldValue("titleMode", v as "text" | "image" | "image_text")
              }
            />
          </Input.Wrapper>
          <FileInput
            label="图片（图片 / 图片+文字 模式生效）"
            placeholder="选择图片文件"
            accept="image/*"
            disabled={imageUploading}
            onChange={(file) => onImageUpload(file)}
          />
          {form.values.imageUrl !== "" ? (
            <Group gap="sm" align="center">
              <Image src={form.values.imageUrl} h={60} w={60} fit="contain" />
              <Button
                variant="light"
                color="red"
                size="xs"
                type="button"
                onClick={() => form.setFieldValue("imageUrl", "")}
              >
                清除图片
              </Button>
            </Group>
          ) : null}
          {languages.map((lang) => (
            <TextInput
              key={lang}
              label={`名称 · ${lang}（扇区文字，留空回退「名称」）`}
              value={form.values.i18nName[lang] ?? ""}
              onChange={(e) =>
                form.setFieldValue("i18nName", {
                  ...form.values.i18nName,
                  [lang]: e.currentTarget.value,
                })
              }
            />
          ))}
          {languages.map((lang) => (
            <TextInput
              key={`sub-${lang}`}
              label={`副标题 · ${lang}（扇区小字；图片+文字 模式不显示）`}
              value={form.values.i18nSubtitle[lang] ?? ""}
              onChange={(e) =>
                form.setFieldValue("i18nSubtitle", {
                  ...form.values.i18nSubtitle,
                  [lang]: e.currentTarget.value,
                })
              }
            />
          ))}
          <SimpleGrid cols={3}>
            <ColorInput label="背景色" {...form.getInputProps("displayColor")} />
            <TextInput
              label="图标字符（如 ★，避免 emoji）"
              {...form.getInputProps("displayIcon")}
            />
            <NumberInput
              label="扇区顺序（小在前）"
              min={0}
              {...form.getInputProps("displayOrder")}
            />
          </SimpleGrid>
        </AdminFormSection>

        <AdminFormSection title="上下架时间">
          <SimpleGrid cols={2}>
            <TextInput
              type="datetime-local"
              label="上架时间（可空）"
              {...form.getInputProps("startsAt")}
            />
            <TextInput
              type="datetime-local"
              label="下架时间（可空）"
              {...form.getInputProps("endsAt")}
            />
          </SimpleGrid>
        </AdminFormSection>

        <AdminFormActions>
          <Button variant="default" type="button" onClick={onClose}>
            取消
          </Button>
          <Button type="submit" loading={isPending || imageUploading}>
            保存
          </Button>
        </AdminFormActions>
      </AdminForm>
    </AdminFormModal>
  );
}
